import {
  BarChart3,
  CalendarDays,
  ClipboardList,
  History,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  Receipt,
  Settings,
  ShieldAlert,
  Store,
  Tag,
  Users,
  Wallet,
  X,
} from "lucide-react";
import { useLocale, useT } from "@/lib/i18n";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, type ComponentType } from "react";
import { toast } from "sonner";

import { isRtlLocale, type Locale, type MemberRole } from "@ticketnjoy/types";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { useLogoutMutation, useMeQuery } from "@/features/organizers/api";
import { ChangePasswordDialog } from "@/features/organizers/change-password-dialog";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

interface DashboardShellProps {
  companyName: string;
  role?: MemberRole;
  children: React.ReactNode;
}

interface NavItem {
  href: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  matchPrefix: string;
  /** Erişebilecek roller. Boşsa herkes görür. */
  roles?: readonly MemberRole[];
}

/**
 * Rol bazlı menü:
 * - Owner: tümü (Ekip ve Finans dahil)
 * - Manager: Ekip ve Finans hariç tümü
 * - Editor: Mağazalar, Etkinlikler, Siparişler (refund yok), Promosyonlar
 * - Accountant: Siparişler ve raporlar (Mağazalar görüntü için ok)
 * - Gate: sadece Check-in (zaten /check-in'e redirect edilir)
 */
function buildNavItems(role: MemberRole | undefined, t: ReturnType<typeof useT>): NavItem[] {
  const items: NavItem[] = [
    {
      href: "/",
      label: t("nav.dashboard"),
      icon: LayoutDashboard,
      matchPrefix: "/__dashboard__", // exact match için unreachable prefix
      roles: ["Owner", "Manager", "Editor", "Accountant"],
    },
    {
      href: "/shops",
      label: t("nav.shops"),
      icon: Store,
      matchPrefix: "/shops",
      roles: ["Owner", "Manager", "Editor", "Accountant"],
    },
    {
      href: "/events",
      label: t("nav.events"),
      icon: CalendarDays,
      matchPrefix: "/events",
      roles: ["Owner", "Manager", "Editor"],
    },
    {
      href: "/orders",
      label: t("nav.orders"),
      icon: Receipt,
      matchPrefix: "/orders",
      roles: ["Owner", "Manager", "Editor", "Accountant"],
    },
    {
      href: "/promotions",
      label: t("nav.promotions"),
      icon: Tag,
      matchPrefix: "/promotions",
      roles: ["Owner", "Manager", "Editor"],
    },
    {
      href: "/check-in",
      label: t("nav.checkIn"),
      icon: QrCode,
      matchPrefix: "/check-in",
      roles: ["Owner", "Manager", "Editor", "Gate"],
    },
    {
      href: "/team",
      label: t("nav.team"),
      icon: Users,
      matchPrefix: "/team",
      roles: ["Owner"],
    },
    {
      href: "/reports",
      label: t("nav.reports"),
      icon: BarChart3,
      // Sadece /reports kök yolu için aktif; /reports/events sub-page kendi
      // sidebar entry'sine aktif olur.
      matchPrefix: "/reports$",
      roles: ["Owner", "Manager", "Accountant"],
    },
    {
      href: "/reports/events",
      label: t("nav.eventReports"),
      icon: ClipboardList,
      matchPrefix: "/reports/events",
      roles: ["Owner", "Manager", "Accountant"],
    },
    {
      href: "/finance",
      label: t("nav.finance"),
      icon: Wallet,
      matchPrefix: "/finance",
      roles: ["Owner"],
    },
    {
      href: "/audit",
      label: t("nav.audit"),
      icon: History,
      matchPrefix: "/audit",
      roles: ["Owner", "Manager"],
    },
    {
      href: "/settings",
      label: t("nav.settings"),
      icon: Settings,
      matchPrefix: "/settings",
      roles: ["Owner", "Manager"],
    },
  ];
  if (!role) return items;
  return items.filter((it) => !it.roles || it.roles.includes(role));
}

function isActive(pathname: string, item: NavItem): boolean {
  if (item.href === "/") {
    return pathname === "/" || pathname.startsWith(item.matchPrefix);
  }
  // "$" suffix — exact match. /reports vs /reports/events ayrımı için:
  // /reports kök linki sadece kendi yolunda aktif olmalı, alt yollarda
  // (/reports/events) kendi sidebar entry'si aktif olur.
  if (item.matchPrefix.endsWith("$")) {
    const base = item.matchPrefix.slice(0, -1);
    return pathname === base;
  }
  return pathname.startsWith(item.matchPrefix);
}

/**
 * Authenticated shell — mobile-first ve RTL-aware.
 *
 * Desktop (lg+): sabit dar sidebar (56px), logical start kenarında.
 * Mobile: hamburger drawer — LTR'de soldan, RTL'de sağdan açılır.
 *
 * Logical CSS properties (start/end) Tailwind v4'te destekleniyor;
 * sidebar RTL'de otomatik karşı tarafa geçer.
 */
export function DashboardShell({ companyName, role, children }: DashboardShellProps) {
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const logout = useLogoutMutation();
  const impersonation = useMeQuery().data?.impersonation;
  const t = useT();
  const locale = useLocale() as Locale;
  const isRtl = isRtlLocale(locale);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPathname, setDrawerPathname] = useState(pathname);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const navItems = buildNavItems(role, t);

  if (drawerPathname !== pathname) {
    setDrawerPathname(pathname);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  async function handleLogout() {
    try {
      await logout.mutateAsync();
      toast.success(t("auth.loggedOut"));
      navigate("/login", { replace: true });
    } catch {
      toast.error(t("common.unexpectedError"));
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <DesktopSidebar pathname={pathname} navItems={navItems} t={t} />

      {drawerOpen && (
        <button
          type="button"
          aria-label={t("common.close")}
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}
      <MobileDrawer
        open={drawerOpen}
        pathname={pathname}
        companyName={companyName}
        navItems={navItems}
        isRtl={isRtl}
        onClose={() => setDrawerOpen(false)}
        onChangePassword={() => {
          setDrawerOpen(false);
          setChangePasswordOpen(true);
        }}
        onLogout={handleLogout}
        isLoggingOut={logout.isPending}
        t={t}
      />

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />

      {/* Sidebar start tarafında — RTL'de ps- sağa geçer */}
      <div className="lg:ps-14">
        <div className="sticky top-0 z-30">
          {impersonation && (
            <ImpersonationBanner
              expiresAt={impersonation.expiresAt}
              onEnd={handleLogout}
              isEnding={logout.isPending}
              t={t}
            />
          )}
          <header className="flex h-14 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-accent lg:hidden"
              aria-label={t("nav.menu")}
            >
              <Menu className="h-5 w-5" />
            </button>

            <Link
              to="/"
              className="flex items-center gap-2 font-semibold lg:hidden"
            >
              <BrandMark />
              <span>{t("app.name")}</span>
            </Link>

            <div className="ms-auto flex items-center gap-2">
              <LocaleSwitcher />
              <ThemeToggle />
              <span className="hidden max-w-[180px] truncate text-sm text-muted-foreground sm:inline">
                {companyName}
              </span>
              <button
                type="button"
                onClick={() => setChangePasswordOpen(true)}
                className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:inline-flex"
                aria-label={t("auth.changePassword.button")}
                title={t("auth.changePassword.button")}
              >
                <KeyRound className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={logout.isPending}
                className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
                aria-label={t("auth.logout")}
              >
                <LogOut className="h-4 w-4 rtl:-scale-x-100" />
                <span className="hidden sm:inline">
                  {logout.isPending ? t("auth.loggingOut") : t("auth.logout")}
                </span>
              </button>
            </div>
          </header>
        </div>

        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}

/* ─────────────────────────── Impersonation banner ─────────────────────────── */

function ImpersonationBanner({
  expiresAt,
  onEnd,
  isEnding,
  t,
}: {
  expiresAt: string;
  onEnd: () => void;
  isEnding: boolean;
  t: ReturnType<typeof useT>;
}) {
  const time = useFormat().time(expiresAt);
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-1.5 text-center text-sm font-medium text-amber-950">
      <span className="inline-flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        {t("auth.impersonation.banner", { time })}
      </span>
      <button
        type="button"
        onClick={onEnd}
        disabled={isEnding}
        className="rounded-md bg-amber-950/10 px-2 py-0.5 hover:bg-amber-950/20 disabled:opacity-60"
      >
        {isEnding ? t("auth.loggingOut") : t("auth.impersonation.end")}
      </button>
    </div>
  );
}

/* ─────────────────────────── Desktop sidebar ─────────────────────────── */

function DesktopSidebar({
  pathname,
  navItems,
  t,
}: {
  pathname: string;
  navItems: NavItem[];
  t: ReturnType<typeof useT>;
}) {
  return (
    <aside className="fixed inset-y-0 start-0 z-30 hidden w-14 flex-col items-center border-e border-sidebar-border bg-sidebar py-3 lg:flex">
      <Link
        to="/"
        className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md text-primary transition-colors hover:bg-sidebar-accent"
        aria-label={t("app.name")}
      >
        <BrandMark />
      </Link>

      <nav className="flex flex-1 flex-col gap-1">
        {navItems.map((item) => (
          <SidebarLink
            key={item.href}
            item={item}
            label={item.label}
            active={isActive(pathname, item)}
          />
        ))}
      </nav>
    </aside>
  );
}

function SidebarLink({
  item,
  label,
  active,
}: {
  item: NavItem;
  label: string;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      className={cn(
        "group relative inline-flex h-10 w-10 items-center justify-center rounded-md text-sidebar-foreground transition-colors",
        active
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      )}
      aria-label={label}
    >
      <Icon className="h-4.5 w-4.5" />
      {/* Hover tooltip — start side'ın dışında, RTL'de otomatik sağdan sola */}
      <span className="pointer-events-none absolute start-full ms-3 hidden rounded-md bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md ring-1 ring-border group-hover:inline-block whitespace-nowrap">
        {label}
      </span>
      {active ? (
        <span
          aria-hidden
          className="absolute start-0 h-5 w-0.5 rounded-e-full bg-primary"
        />
      ) : null}
    </Link>
  );
}

/* ─────────────────────────── Mobile drawer ─────────────────────────── */

interface MobileDrawerProps {
  open: boolean;
  pathname: string;
  companyName: string;
  navItems: NavItem[];
  isRtl: boolean;
  onClose: () => void;
  onChangePassword: () => void;
  onLogout: () => void;
  isLoggingOut: boolean;
  t: ReturnType<typeof useT>;
}

function MobileDrawer({
  open,
  pathname,
  companyName,
  navItems,
  isRtl,
  onClose,
  onChangePassword,
  onLogout,
  isLoggingOut,
  t,
}: MobileDrawerProps) {
  // translate-x açık/kapalı yönü dile bağlı — RTL'de zıt yöne kaydır.
  const closedTx = isRtl ? "translate-x-full" : "-translate-x-full";
  return (
    <aside
      className={cn(
        "fixed inset-y-0 start-0 z-50 flex w-72 max-w-[80vw] flex-col border-e border-sidebar-border bg-sidebar shadow-xl transition-transform duration-200 ease-out lg:hidden",
        open ? "translate-x-0" : closedTx,
      )}
      aria-hidden={!open}
      inert={!open}
    >
      <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <BrandMark />
          <span>{t("app.name")}</span>
        </Link>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 p-3">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(pathname, item);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "inline-flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                active
                  ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent",
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-sidebar-border p-3">
        <p className="mb-2 truncate px-3 text-xs text-muted-foreground">
          {companyName}
        </p>
        <button
          type="button"
          onClick={onChangePassword}
          className="inline-flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
        >
          <KeyRound className="h-4 w-4" />
          {t("auth.changePassword.button")}
        </button>
        <button
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          className="inline-flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent disabled:opacity-60"
        >
          <LogOut className="h-4 w-4 rtl:-scale-x-100" />
          {isLoggingOut ? t("auth.loggingOut") : t("auth.logout")}
        </button>
      </div>
    </aside>
  );
}

/* ─────────────────────────── Brand mark ─────────────────────────── */

function BrandMark() {
  return (
    <svg viewBox="0 0 640 640" aria-hidden className="h-5 w-5">
      <rect x="0" y="0" width="640" height="640" rx="128" fill="currentColor" />
      <path d="M200 100H340L280 193.5L220 287L280 380.5H140L80 287L140 193.5L200 100Z" fill="white" />
      <path d="M440 540L300 540L360 446.5L420 353L360 259.5L500 259.5L560 353L500 446.5L440 540Z" fill="white" />
    </svg>
  );
}
